import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ReportsService } from './reports.service';

type ReportType = 'deliveries' | 'maintenance' | 'supply' | 'staff' | 'projects';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  loading = false;
  reportType: ReportType = 'deliveries';
  rows: any[] = [];
  generatedAt = new Date();
  riders: any[] = [];
  vehicles: any[] = [];
  productTypes: any[] = [];
  productItems: any[] = [];
  staffList: any[] = [];
  projectList: any[] = [];
  supplyAnalytics: any = null;

  filters = {
    from: '',
    to: '',
    isPaid: 'all',
    search: '',
    riderName: '',
    vehicleNumber: '',
    vehicleId: '',
    productTypeId: '',
    productItemId: '',
    staffId: '',
    requestStatus: 'all',
    requestType: 'all',
    projectId: '',
    taskStatus: 'all'
  };

  deliveryColumns: string[] = [
    'orderDate',
    'customerName',
    'contactNumber',
    'deliveryLocation',
    'amount',
    'isPaid',
    'isCancelled'
  ];

  maintenanceColumns: string[] = [
    'serviceDate',
    'vehiclePlate',
    'recordType',
    'description',
    'cost'
  ];

  supplyColumns: string[] = [
    'dispatchDate',
    'productTypeName',
    'productItemName',
    'buyerName',
    'origin',
    'destination',
    'unitCount',
    'unitPrice',
    'commissionAmount',
    'totalAmount',
    'netAmount'
  ];

  staffColumns: string[] = [
    'staffName',
    'department',
    'position',
    'isActive',
    'requestType',
    'status',
    'amount',
    'title',
    'directedToStaffName',
    'requestedAt'
  ];

  projectColumns: string[] = [
    'projectName',
    'clientName',
    'leadStaffName',
    'projectStatus',
    'taskTitle',
    'taskStatus',
    'taskPriority',
    'assignedStaffName',
    'taskProgressPercent',
    'taskDueDate',
    'taskUpdatedAt'
  ];

  constructor(private reports: ReportsService) {}

  ngOnInit(): void {
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.removePrintMode();
  }

  private loadFilterOptions(): void {
    forkJoin({
      riders: this.reports.getRiders(),
      vehicles: this.reports.getVehicles(),
      productTypes: this.reports.getSupplyProducts(),
      staff: this.reports.getStaffList(),
      projects: this.reports.getProjectList()
    }).subscribe({
      next: ({ riders, vehicles, productTypes, staff, projects }) => {
        this.riders = riders || [];
        this.vehicles = vehicles || [];
        this.productTypes = productTypes || [];
        this.staffList = (staff || []).map((s: any) => ({ staffId: s.id, staffName: s.fullName }));
        this.projectList = projects || [];
      },
      error: (err) => {
        console.error('[Reports] failed to load filter options:', err);
      }
    });
  }

  runReport(): void {
    this.loading = true;

    const request$ =
      this.reportType === 'deliveries'
        ? this.reports.getDeliveryReport(this.filters)
        : this.reportType === 'maintenance'
          ? this.reports.getMaintenanceReport(this.filters)
          : this.reportType === 'supply'
            ? this.reports.getSupplyReport(this.filters)
            : this.reportType === 'staff'
              ? this.reports.getStaffReport({
                  ...this.filters,
                  status: this.filters.requestStatus,
                  requestType: this.filters.requestType
                })
              : this.reports.getProjectReport(this.filters);

    request$.subscribe({
      next: (res) => {
        this.rows = res || [];
        if (this.reportType === 'supply') {
          this.reports.getSupplyAnalytics(this.filters).subscribe({
            next: (analytics) => this.supplyAnalytics = analytics || null,
            error: () => this.supplyAnalytics = null
          });
        } else {
          this.supplyAnalytics = null;
        }
        this.generatedAt = new Date();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error('[Reports] failed:', err);
        Swal.fire('Error', 'Failed to load report data.', 'error');
      }
    });
  }

  resetFilters(): void {
    this.filters = {
      from: '',
      to: '',
      isPaid: 'all',
      search: '',
      riderName: '',
      vehicleNumber: '',
      vehicleId: '',
      productTypeId: '',
      productItemId: '',
      staffId: '',
      requestStatus: 'all',
      requestType: 'all',
      projectId: '',
      taskStatus: 'all'
    };
    this.supplyAnalytics = null;
    this.rows = [];
  }

  printReport(): void {
    if (!this.rows.length) {
      Swal.fire('Nothing to print', 'Run a report first.', 'info');
      return;
    }
    document.body.classList.add('report-print-mode');

    const afterPrintHandler = (): void => {
      this.removePrintMode();
      window.removeEventListener('afterprint', afterPrintHandler);
    };

    window.addEventListener('afterprint', afterPrintHandler);

    setTimeout(() => {
      window.print();
    }, 0);
  }

  get currentColumns(): string[] {
    if (this.reportType === 'deliveries') return this.deliveryColumns;
    if (this.reportType === 'maintenance') return this.maintenanceColumns;
    if (this.reportType === 'supply') return this.supplyColumns;
    if (this.reportType === 'projects') return this.projectColumns;
    return this.staffColumns;
  }

  get title(): string {
    if (this.reportType === 'deliveries') return 'Delivery Report';
    if (this.reportType === 'maintenance') return 'Maintenance Report';
    if (this.reportType === 'supply') return 'Supply Report';
    if (this.reportType === 'projects') return 'Project Report';
    return 'Staff Report';
  }

  get totalAmount(): number {
    if (this.reportType !== 'deliveries') return 0;
    return this.rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  }

  get totalCost(): number {
    if (this.reportType !== 'maintenance') return 0;
    return this.rows.reduce((sum, r) => sum + Number(r.cost || 0), 0);
  }

  get totalSupplyGross(): number {
    if (this.reportType !== 'supply') return 0;
    return this.rows.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0);
  }

  get totalSupplyNet(): number {
    if (this.reportType !== 'supply') return 0;
    return this.rows.reduce((sum, r) => sum + Number((r.totalAmount || 0) - (r.commissionAmount || 0)), 0);
  }

  get totalStaffRequests(): number {
    if (this.reportType !== 'staff') return 0;
    return this.rows.filter(r => (r.requestType || '-') !== '-').length;
  }

  get totalProjectTasks(): number {
    if (this.reportType !== 'projects') return 0;
    return this.rows.length;
  }

  get completedProjectTasks(): number {
    if (this.reportType !== 'projects') return 0;
    return this.rows.filter(r => ['done', 'completed'].includes((r.taskStatus || '').toLowerCase())).length;
  }

  onSupplyTypeChange(): void {
    const selectedType = this.productTypes.find(t => String(t.id) === String(this.filters.productTypeId));
    this.productItems = selectedType?.items || [];
    this.filters.productItemId = '';
  }

  private removePrintMode(): void {
    document.body.classList.remove('report-print-mode');
  }
}
